import React from "react";
import { ApolloProvider } from "react-apollo";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";
import {
  Security,
  SecureRoute,
  LoginCallback,
  AuthService,
} from "@okta/okta-react";
import { Home } from "./Container";
import {
  NoAccessScreen,
  NoAccessPerfTesting,
  NotFound,
  AsaAccessError,
  Logout,
} from "./Component";
import { UserContextProvider } from "./Context/UserContext";
import ModalDialogWithContext from "./Component/Dialog";
import ModalDialogProvider from "./Context/ModalDialog/ModalDialogProvider";
import i18next from "i18next";
import client from "./API/graphqlClient";
import { updateAuthState } from "./API/autStateHolder";

require("dotenv").config();

const {
  REACT_APP_OKTA_ISSUER: issuer,
  REACT_APP_OKTA_CLIENT_ID: clientId,
  REACT_APP_OKTA_AUTH_SERVER_ID: serverId,
  REACT_APP_REDIRECT_URL: redirectUri,
} = process.env || {};

const config = {
  issuer:
    serverId && serverId.length > 0 ? `${issuer}/oauth2/${serverId}` : issuer,
  clientId,
  pkce: true,
  redirectUri,
  scopes: ["openid", "email", "profile", "groups"],
  responseType: ["code"],
  tokenManager: {
    secure: true,
    storage: "cookie",
  },
};

const authService = new AuthService(config);

// Retry i18next's translation fetch once auth is ready, since a token-less
// 401 on boot otherwise never gets retried.
const reloadTranslationsOnceAuthenticated = () => {
  const { state } = i18next.services.backendConnector;
  const stillPending = Object.values(state).some((s) => s === 1);
  if (stillPending) {
    setTimeout(reloadTranslationsOnceAuthenticated, 100);
    return;
  }
  Object.keys(state).forEach((key) => {
    if (state[key] < 0) state[key] = 0;
  });
  i18next.reloadResources();
};

let wasAuthenticated = false;
authService.on("authStateChange", (authState) => {
  updateAuthState(authState);
  if (authState?.isAuthenticated && !wasAuthenticated) {
    reloadTranslationsOnceAuthenticated();
  }
  wasAuthenticated = !!authState?.isAuthenticated;
});

const App = () => {
  return (
    <ApolloProvider client={client}>
      <ModalDialogProvider>
        <ModalDialogWithContext />
        <BrowserRouter>
          <Security authService={authService}>
            <UserContextProvider>
              <Switch>
                <SecureRoute
                  exact
                  path="/"
                  component={() => <Redirect to="/glc" />}
                />
                <SecureRoute path="/glc" component={Home} />
                <Route path="/loginCallback" component={LoginCallback} />
                <Route path="/noAccess" exact component={NoAccessScreen} />
                <Route
                  path="/noAccessPerfTesting"
                  exact
                  component={NoAccessPerfTesting}
                />
                <Route path="/notFound" exact component={NotFound} />
                <Route path="/asaError" exact component={AsaAccessError} />
                <Route path="/logout" exact component={Logout} />
                <Route component={NotFound} />
              </Switch>
            </UserContextProvider>
          </Security>
        </BrowserRouter>
      </ModalDialogProvider>
    </ApolloProvider>
  );
};

export default App;
